import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@worldcoo/ui/hooks/use-prefers-reduced-motion';

/** Paired with {@link offsetForStep}: if they disagree, a click shows one step and the next wheel snaps back. */
export function stepFromOffset(offset: number, travel: number, steps: number) {
	if (travel <= 0 || steps <= 1) return 0;
	const progress = Math.min(1, Math.max(0, offset / travel));
	// `steps - 1` so the last step is reached exactly at the bottom rather than one step early.
	return Math.round(progress * (steps - 1));
}

export function offsetForStep(index: number, travel: number, steps: number) {
	if (steps <= 1) return 0;
	return (Math.min(steps - 1, Math.max(0, index)) / (steps - 1)) * travel;
}

/**
 * The sticky child is pinned for exactly the section's scrollable length, so the fraction scrolled IS
 * the progress. Read in `requestAnimationFrame` so a fast wheel doesn't force a layout per event.
 * `enabled` is not derived from layout: the extra height creates the travel, so asking for travel
 * first would never start.
 */
export function useScrollStep(steps: number) {
	const ref = useRef<HTMLDivElement>(null);
	const [active, setActive] = useState(0);
	const enabled = !usePrefersReducedMotion();

	/** Null while unpinnable. */
	const geometry = useCallback(() => {
		const element = ref.current;
		if (!element) return null;
		const rect = element.getBoundingClientRect();
		// Measured off the child, not assumed one viewport: a `min-h-screen` child would reach its last step early.
		const pinned = element.firstElementChild;
		const travel = rect.height - (pinned?.getBoundingClientRect().height ?? window.innerHeight);
		if (travel <= 0) return null;
		return { top: rect.top + window.scrollY, travel };
	}, []);

	useEffect(() => {
		if (!ref.current || !enabled) return;

		let frame = 0;
		const measure = () => {
			frame = 0;
			const box = geometry();
			if (!box) return;
			setActive(stepFromOffset(window.scrollY - box.top, box.travel, steps));
		};

		const onScroll = () => {
			if (frame) return;
			frame = requestAnimationFrame(measure);
		};

		measure();
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll);
		return () => {
			if (frame) cancelAnimationFrame(frame);
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
		};
	}, [steps, enabled, geometry]);

	/**
	 * Moves the page rather than setting the index, so scroll stays the single source of truth. Reduced
	 * motion must set it: with no pin no scroll ever produces an index, and that visitor would be stuck on
	 * the first card.
	 */
	const goTo = useCallback(
		(index: number) => {
			if (!enabled) {
				setActive(Math.min(steps - 1, Math.max(0, index)));
				return;
			}
			const box = geometry();
			if (!box) return;
			window.scrollTo({ top: box.top + offsetForStep(index, box.travel, steps), behavior: 'smooth' });
		},
		[enabled, geometry, steps],
	);

	return { ref, active, enabled, goTo };
}
